// src/components/product/ProductFilter.jsx
import React, { useState, useEffect } from 'react';
import collegeService from '../../services/collegeService';
import Loading from '../common/Loading';

/**
 * Product Filter Component
 * 
 * 🎓 MAIN FEATURE: Filter products by college
 * 
 * Filters:
 * - College dropdown (IIT, NIT, BITS, IIM)
 * - Category filter (T-Shirt, Hoodie, All)
 * - Search input
 */
const ProductFilter = ({ onFilterChange, currentFilters }) => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Load colleges on mount
  useEffect(() => {
    fetchColleges();
  }, []);
  
  const fetchColleges = async () => {
    try {
      const data = await collegeService.getAllColleges();
      setColleges(data);
    } catch (error) {
      console.error('Error fetching colleges:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCollegeChange = (e) => {
    const collegeId = e.target.value ? parseInt(e.target.value) : null;
    onFilterChange({ ...currentFilters, collegeId });
  };
  
  const handleCategoryChange = (e) => {
    const category = e.target.value || null;
    onFilterChange({ ...currentFilters, category });
  };
  
  const handleSearchChange = (e) => {
    const search = e.target.value;
    onFilterChange({ ...currentFilters, search });
  };
  
  const clearFilters = () => {
    onFilterChange({ collegeId: null, category: null, search: '' });
  };
  
  if (loading) {
    return <Loading size="sm" />;
  }
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex flex-col md:flex-row gap-4">
        {/* 🎓 College Filter - MAIN FEATURE */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by College
          </label>
          <select
            value={currentFilters.collegeId || ''}
            onChange={handleCollegeChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Colleges</option>
            {colleges.map((college) => (
              <option key={college.id} value={college.id}>
                {college.name} ({college.code})
              </option>
            ))}
          </select>
        </div>
        
        {/* Category Filter */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={currentFilters.category || ''}
            onChange={handleCategoryChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            <option value="T_SHIRT">T-Shirts</option>
            <option value="HOODIE">Hoodies</option>
          </select>
        </div>
        
        {/* Search Input */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search
          </label>
          <input
            type="text"
            value={currentFilters.search || ''}
            onChange={handleSearchChange}
            placeholder="Search products..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        {/* Clear Filters Button */}
        <div className="flex items-end">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Active Filters Display */}
      {(currentFilters.collegeId || currentFilters.category || currentFilters.search) && (
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm text-gray-600">Active filters:</span>
          
          {currentFilters.collegeId && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              {colleges.find(c => c.id === currentFilters.collegeId)?.name}
              <button
                onClick={() => onFilterChange({ ...currentFilters, collegeId: null })}
                className="hover:text-blue-900"
              >
                ×
              </button>
            </span>
          )}
          
          {currentFilters.category && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              {currentFilters.category === 'T_SHIRT' ? 'T-Shirts' : 'Hoodies'}
              <button
                onClick={() => onFilterChange({ ...currentFilters, category: null })}
                className="hover:text-green-900"
              >
                ×
              </button>
            </span>
          )}
          
          {currentFilters.search && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              "{currentFilters.search}"
              <button
                onClick={() => onFilterChange({ ...currentFilters, search: '' })}
                className="hover:text-purple-900"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductFilter;